import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import type { Job } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { subscribeToAllJobs } from '../lib/jobs.js';
import { NewImportForm } from '../components/NewImportForm.js';
import { JobsTable } from '../components/JobsTable.js';
import { confettiBlast } from '../lib/confetti.js';

const ACTIVE_STATUSES = new Set([
  'queued',
  'running',
  'awaiting_decrypt',
  'decrypt_moving',
  'decrypt_extracting',
  'decrypt_restoring',
  'decrypt_exporting',
]);

// Soft-deleted jobs (data wiped, record kept as a log) live in their own
// section, including the in-progress `deleting` and errored `delete_failed`.
const DELETED_STATUSES = new Set(['deleting', 'deleted', 'delete_failed']);

export function FtpImportPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Array<Job & { id: string }>>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [lastCreated, setLastCreated] = useState<string | null>(null);

  // Remembers the last-seen status of each job so we can fire confetti the
  // moment a job flips to `complete`. Seeded on the first snapshot so we don't
  // celebrate imports that were already finished when the page loaded.
  const prevStatusRef = useRef<Map<string, Job['status']> | null>(null);

  const [search, setSearch] = useState('');
  // Which log the shared bottom table shows — completed imports or deleted ones.
  const [logView, setLogView] = useState<'completed' | 'deleted'>('completed');

  const activeAll = jobs.filter((j) => ACTIVE_STATUSES.has(j.status));
  const deletedAll = jobs.filter((j) => DELETED_STATUSES.has(j.status));
  const completedAll = jobs.filter(
    (j) => !ACTIVE_STATUSES.has(j.status) && !DELETED_STATUSES.has(j.status),
  );

  // Matches the text the row actually shows: name, system, who ran it and the
  // S3 destination. `jobs` itself stays unfiltered — the new-import form uses it
  // to spot duplicate destinations, and confetti needs every status transition.
  const term = search.trim().toLowerCase();
  const matches = (j: Job & { id: string }) => {
    if (!term) return true;
    const dest = j.s3Destination
      ? `${j.s3Destination.bucket}/${j.s3Destination.prefix}`
      : '';
    return `${j.clientName} ${j.sourceSystem} ${j.createdBy?.displayName ?? ''} ${dest}`
      .toLowerCase()
      .includes(term);
  };

  const activeJobs = activeAll.filter(matches);
  const completedJobs = completedAll.filter(matches);
  const deletedJobs = deletedAll.filter(matches);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToAllJobs(
      (j) => {
        const prev = prevStatusRef.current;
        if (prev) {
          const justCompleted = j.some(
            (job) => job.status === 'complete' && prev.get(job.id) !== 'complete',
          );
          if (justCompleted) confettiBlast();
        }
        prevStatusRef.current = new Map(j.map((job) => [job.id, job.status]));

        setJobs(j);
        setLoadingJobs(false);
      },
      () => setLoadingJobs(false),
    );
    return unsub;
  }, [user]);

  function handleCreated(jobId: string) {
    setLastCreated(jobId);
    setFormOpen(false);
  }

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#37352f] tracking-tight">
            FTP Import
          </h1>
          <p className="mt-0.5 text-sm text-[#73726e]">
            Stream data from a client FTP/SFTP server directly to S3.
          </p>
        </div>
        <button
          onClick={() => setFormOpen((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] transition-colors"
        >
          <Plus size={14} />
          New import
        </button>
      </div>

      {/* Collapsible form */}
      {formOpen && (
        <div className="mb-6 border border-[#e9e9e7] rounded-lg p-5 bg-[#fafafa]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#37352f]">
              New import
            </h2>
            <button
              onClick={() => setFormOpen(false)}
              className="text-[#aeaaa4] hover:text-[#73726e]"
            >
              <ChevronDown size={16} />
            </button>
          </div>
          <NewImportForm onCreated={handleCreated} existingJobs={jobs} />
        </div>
      )}

      {lastCreated && (
        <div className="mb-4 px-3 py-2 bg-[#e5f5f2] border border-[#b7ddd8] rounded text-xs text-[#0f7b6c]">
          Import started — job ID{' '}
          <code className="font-mono">{lastCreated}</code>. Status updates
          live below.
        </div>
      )}

      {/* ── In progress ── */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 rounded-full bg-[#2383e2] animate-pulse shrink-0" />
          <h2 className="text-sm font-semibold text-[#37352f]">In progress</h2>
          {!loadingJobs && (
            <span className="text-xs text-[#aeaaa4]">
              ({term ? `${activeJobs.length} of ${activeAll.length}` : activeJobs.length})
            </span>
          )}
        </div>
        <div className="border border-[#e9e9e7] rounded-lg overflow-hidden">
          <JobsTable
            jobs={activeJobs}
            loading={loadingJobs}
            emptyMessage={
              term ? 'No active imports match your filter.' : 'No active imports.'
            }
          />
        </div>
      </div>

      {/* ── Completed / Deleted logs (toggled) ── */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          {/* Toggle between the completed and deleted logs in one place. */}
          <div className="inline-flex items-center gap-1 p-0.5 bg-[#f1f1ef] rounded-md">
            {([
              ['completed', 'Completed', completedJobs.length, completedAll.length] as const,
              ['deleted', 'Deleted', deletedJobs.length, deletedAll.length] as const,
            ]).map(([key, label, shown, total]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLogView(key)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  logView === key
                    ? 'bg-white text-[#37352f] shadow-sm'
                    : 'text-[#73726e] hover:text-[#37352f]'
                }`}
              >
                {label}
                {!loadingJobs && (
                  <span className="ml-1.5 text-[#aeaaa4]">
                    {term ? `${shown} of ${total}` : shown}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Filters every table, so the In progress count above moves too. */}
          {jobs.length > 0 && (
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name, system, person or S3 path…"
              className="w-80 px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
            />
          )}
        </div>
        {logView === 'deleted' && (
          <p className="text-xs text-[#73726e] mb-3">
            Data removed from S3; the record is kept as a log.
          </p>
        )}
        <div className="border border-[#e9e9e7] rounded-lg overflow-hidden">
          {logView === 'completed' ? (
            <JobsTable
              jobs={completedJobs}
              loading={loadingJobs}
              hideProgress
              emptyMessage={
                term
                  ? 'No completed imports match your filter.'
                  : 'No completed imports yet.'
              }
            />
          ) : (
            <JobsTable
              jobs={deletedJobs}
              loading={loadingJobs}
              showDeletedBy
              emptyMessage={
                term
                  ? 'No deleted imports match your filter.'
                  : 'No deleted imports.'
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
